/**
 * useProductsBySkus - Fetch products by SKU list
 *
 * Uses standard Magento products query with SKU filter.
 * Returns transformed Product[] for ProductCard components.
 */
import { useQuery } from '@tanstack/react-query'

import { homepageApi, transformMagentoProductsToProducts } from '../../lib'
import { homepageKeys } from '../queryKeys'

import type { ProductsData } from '../../lib/apiClient'
import type { Product } from '../../types'

export interface UseProductsBySkusOptions {
  /** Array of SKUs to fetch */
  skus: string[]
  /** Maximum number of products to fetch */
  pageSize: number
  /** Enable/disable the query */
  enabled?: boolean
}

export interface UseProductsBySkusResult {
  /** Transformed products for ProductCard */
  products: Product[]
  /** Total count of products found */
  totalCount: number
  /** Loading state */
  isLoading: boolean
  /** Error state */
  isError: boolean
  /** Error object if any */
  error: Error | null
}

/**
 * Fetch products by SKU list using standard Magento products query
 *
 * @example
 * const { products, isLoading } = useProductsBySkus({
 *   skus: ['SKU-001', 'SKU-002', 'SKU-003'],
 *   pageSize: 10,
 * })
 */
export const useProductsBySkus = ({
  skus,
  pageSize,
  enabled = true,
}: UseProductsBySkusOptions): UseProductsBySkusResult => {
  const { data, isLoading, isError, error } = useQuery<ProductsData>({
    queryKey: homepageKeys.productsBySkus(skus, pageSize),
    queryFn: () => homepageApi.getProductsBySkus(skus, pageSize),
    enabled: enabled && skus.length > 0,
  })

  const magentoProducts = data?.products?.items ?? []
  const products = transformMagentoProductsToProducts(magentoProducts)
  const totalCount = data?.products?.total_count ?? 0

  return {
    products,
    totalCount,
    isLoading,
    isError,
    error: error ?? null,
  }
}
